import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID, ViewChild, signal, effect, computed, ElementRef, DestroyRef, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { AvatarModule } from 'primeng/avatar';
import { AvatarGroupModule } from 'primeng/avatargroup';
import { ThemeService } from '../../services/theme.service';
import { AppStateService } from '../../services/app-state.service';
import { AuthOAuthService } from '../../services/oauth.service';
import { timer } from 'rxjs';
import { NotificationsMenuComponent } from "../notifications-menu/notifications-menu.component";
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { FocusManagementService } from '../../services/focus-management.service';
import { SseService } from '../../services/sse.service';
import { Button } from "primeng/button";
import { UserwaySettingsService } from '../../services/userway-settings.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface Language {
  name: string;
  code: string;
}

@Component({
  selector: 'app-header',
  imports: [CommonModule, RouterModule, FormsModule, AvatarModule, AvatarGroupModule, ToggleSwitchModule, SelectModule, InputTextModule, InputIconModule, IconFieldModule, NotificationsMenuComponent, OverlayBadgeModule, Button],
  templateUrl: './header.component.html',
})
export class HeaderComponent implements OnInit, OnDestroy {
  @ViewChild('profileBtn') profileBtn!: ElementRef;
  @ViewChild('profileMenu') profileMenu!: ElementRef;
  languages: Language[] | undefined;
  isDarkMode: boolean = false;
  selectedLanguage: Language | undefined;
  items: any[] | undefined;
  private readonly destroyRef = inject(DestroyRef);
  private readonly isBrowser: boolean;
  showNotificationsMenu = signal<boolean>(false);
  showProfileMenu = signal<boolean>(false);
  notificationsUnreadCount = signal<number>(0);
  mobileMenuOpen = signal<boolean>(false);

  userInitials = computed(() => {
    const user = this.appStateService.currentUser();
    if (!user?.fullName) return '?';

    const names = user.fullName.trim().split(/\s+/);

    if (names.length >= 2) {
      return (names[0][0] + names.at(-1)![0]).toUpperCase();
    }

    return names[0][0].toUpperCase();
  });


  constructor(
    private readonly themeService: ThemeService,
    private readonly router: Router,
    public readonly appStateService: AppStateService,
    private readonly oauthService: AuthOAuthService,
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    private readonly sseService: SseService,
    private readonly focusManagementService: FocusManagementService,
    public readonly userwaySettings: UserwaySettingsService
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.subscribeNotificationEvents();
  }

  ngOnInit() {
    window.addEventListener('click', this.handleClickOutside);
    this.sseService.connectToServerSideEvent();

    this.languages = [
      { name: 'Hindi', code: 'NY' },
      { name: 'English', code: 'RM' },
      { name: 'Telugu', code: 'LDN' },
      { name: 'Marathi', code: 'IST' },
    ];
    this.items = [
      {
        label: 'Profile',
        icon: 'ti ti-user',
        command: () => this.navigateToProfile()
      },
      {
        label: 'Logout',
        icon: 'ti ti-logout',
        command: () => this.logout()
      }
    ];
    
    this.subscribeToDarkMode();
    
  }

  handleClickOutside = (event: MouseEvent) => {
    const menuBtn = this.profileBtn?.nativeElement;
    const menu = this.profileMenu?.nativeElement
    const targetEl = event.target as HTMLElement;
    const clickedOnMenu = menu?.contains(targetEl) || menuBtn?.contains(targetEl);
    if (clickedOnMenu) return;
    timer(0).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.showProfileMenu.set(false));
  };

  subscribeNotificationEvents() {
    effect(() => {
      this.notificationsUnreadCount.set(this.sseService.notificationsUnreadCount());
    }, {allowSignalWrites: true});
  }

  subscribeToDarkMode() {
    this.themeService.isDarkMode$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(isDark => {
        this.isDarkMode = isDark;
      });
  }

  toggleMenu() {
    this.showNotificationsMenu.set(false);
    this.showProfileMenu.set(!this.showProfileMenu());
  }

  onThemeToggle() {
    this.themeService.toggleTheme();
  }
  
  navigateToProfile() {
    if (this.appStateService.isLearner()) {
      this.router.navigate(['/learner/profile']);
    } else {
      this.router.navigate(['home/profile']);
    }
  }

  navigateToProfileRoute(): string {
    if (this.appStateService.isLearner()) {
      return '/learner/profile';
    } else if (this.appStateService.isTrainer()) {
      return '/trainer/profile';
    }
    return '/';
  }
  
  logout() {
    if (this.isBrowser) {
      // Revoke OAuth token from Authentik
      this.oauthService.logout().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          console.log('[AUTH] Logout completed');
          // Clear user data using AppStateService
          this.appStateService.clearUser();

          // Navigate to login page
          this.router.navigate(['/login']);
        },
        error: (error) => {
          console.error('[AUTH] Logout error:', error);
          // Still clear user data and navigate even if logout fails
          this.appStateService.clearUser();
          this.router.navigate(['/login']);
        }
      });
    }
  }
  
  get logoSrc(): string {
    if(this.isDarkMode) {
      if(this.userwaySettings.isLightContrast() && !this.userwaySettings.value.highlightLinks) return './assets/imgs/logo-dark.svg';
      return './assets/imgs/logo-light.svg';
    } else {
      if(this.userwaySettings.isDarkContrast() || this.userwaySettings.value.highlightLinks) return './assets/imgs/logo-light.svg';
      return'./assets/imgs/logo-dark.svg';
    }
  }

  toggleNotifications() {
    this.showProfileMenu.set(false);
    this.showNotificationsMenu.set(!this.showNotificationsMenu());
  }

  /**
   * Check if current route is admin review page
   */
  isAdminReviewPage(): boolean {
    return this.router.url.includes('/admin/courses/') && this.router.url.includes('/review');
  }


  closeNotifications() {
    this.showNotificationsMenu.set(false);
    this.focusManagementService.focusNewElement('#notifications-icon', 'Focused moved to notifications icon')
  }

  toggleMobileMenu() {
    this.showProfileMenu.set(!this.showProfileMenu());
    this.mobileMenuOpen.set(!this.mobileMenuOpen());
  }

  ngOnDestroy() {
    this.sseService.closeSSEConnection(true);
    window.removeEventListener('click', this.handleClickOutside);
  }
}
